import { Injectable } from "@angular/core";
import { StorageService } from "./storage.service";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "@assoanalytics/app/shared/services/snackbar/snackbar.service";
import { AclService } from "../acl/acl.service";
import { Router } from "@angular/router";
import { AuthService } from "./auth.service";
import { UserLogin, UserOAuthData } from "../models/user/user-login";
import { UserDataService } from "./api/user-data/user-data.service";
import { NGXLogger } from "ngx-logger";
import { currentUrlType } from "../models/current-url";
import { NavService } from "./nav.service";
import { firstValueFrom, lastValueFrom } from "rxjs";
import { AlertDialogService } from "@assoanalytics/app/shared/services/alert-dialog/alert-dialog.service";
import { TokenService } from "./token.service";
import { PasswordChangeSuggestModalComponent } from "@assoanalytics/app/modules/user-details/components/password-change-suggest-modal/password-change-suggest-modal.component";

@Injectable({
  providedIn: "root",
})
export class LoginService {
  diffDaysToPasswordExpiration: number | null = null;

  constructor(
    private authService: AuthService,
    private userDataService: UserDataService,
    private aclService: AclService,
    private router: Router,
    private storageService: StorageService,
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
    private logger: NGXLogger,
    private navService: NavService,
    private alertService: AlertDialogService,
    private tokenService: TokenService,
  ) {}

  async postLoginActions(
    loginData: UserLogin,
    notifiySuccess: boolean = true,
    redirectUrl: currentUrlType = { url: "" },
  ) {
    this.saveLoginData(loginData);
    await this.loginSucceded(notifiySuccess, redirectUrl);
  }

  private saveLoginData(data: UserLogin) {
    this.authService.setTokens(data.access_token);
    this.authService.setAclData(data.acl);
  }

  private async loginSucceded(
    notifiySuccess: boolean,
    redirectUrl: currentUrlType,
  ) {
    const canReceiveBonus = await this.aclService.canUseFeatureAsync(
      "campaigns",
      "receiveBonus",
    );

    const authUserData = await this.getAuthUserData();
    const isFirstAccess = await this.isFirstAccess();
    const isPasswordExpired = authUserData.password_expired;
    const passwordIsExpiringSoon =
      await this.shouldSuggestPasswordChange(authUserData);

    if (notifiySuccess) {
      this.snackbarService.success(
        this.translateService.instant("MODULE_LOGIN.LOGIN_SUCCESS"),
      );
    }

    if (isFirstAccess) {
      this.navService.isFirstAccess.next(isFirstAccess);
      return this.router.navigate(["/anagrafica/modifica-password"]);
    }

    if (isPasswordExpired) {
      this.navService.isPasswordExpired.next(isPasswordExpired);
      return this.router.navigate(["/anagrafica/modifica-password"]);
    } else if (passwordIsExpiringSoon && !authUserData.strong_auth) {
      this.alertService.openModal(
        PasswordChangeSuggestModalComponent,
        { expiringInto: this.diffDaysToPasswordExpiration },
        true,
      );
    }

    this.storageService.removeMfaData();
    if (redirectUrl.url.length && redirectUrl.url !== "/login") {
      return this.router.navigate([redirectUrl.url], {
        queryParams: {
          ...redirectUrl.params,
        },
      });
    } else {
      return this.router.navigate(["/dashboard"]);
    }
  }

  private async isFirstAccess(): Promise<boolean> {
    const currentUser = this.authService.currentUser.value;
    if (!currentUser?.id) {
      this.logger.warn(
        "Skipping first access check: current user not available after login",
      );
      return false;
    }
    const userId = currentUser.id;

    if (currentUser.isUser()) {
      const userData = await this.userDataService.getCurrentUserData(userId);
      return userData.firstAccess;
    }

    return false;
  }

  private async getAuthUserData(): Promise<UserOAuthData> {
    const userData = await lastValueFrom(this.authService.getUser());
    this.authService.setCurrentUserFromOAuthData(userData);
    await this.userDataService.currentUserAuthData.next(userData);
    return userData;
  }

  /**
   * Controlla se suggerire il cambio password (20-1 giorni prima della scadenza)
   */
  private async shouldSuggestPasswordChange(
    authUserData: UserOAuthData,
  ): Promise<boolean> {
    // Data ultima modifica password
    const lastPasswordMod = authUserData.last_password_modification;
    if (!lastPasswordMod) return false;

    // Ottieni data emissione token (iat)
    const tokenData = this.tokenService.getTokenData();
    if (!tokenData?.iat) return false;
    const tokenIat = tokenData.iat * 1000; // iat in secondi -> ms

    // Data scadenza software = ultima modifica password + 90 giorni
    const passwordModDate = new Date(lastPasswordMod);
    const expirationDate = new Date(
      passwordModDate.getTime() + 90 * 24 * 60 * 60 * 1000,
    );
    const iatDate = new Date(tokenIat);

    // Differenza giorni tra scadenza e emissione token
    const diffMs = expirationDate.getTime() - iatDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    this.diffDaysToPasswordExpiration = diffDays;
    // Range: tra 20 e 1 giorni prima della scadenza
    if (diffDays <= 20 && diffDays >= 1) {
      // Controllo cookie e scadenza
      const cookieName = "password_expiration_suggestion";
      const cookies = document.cookie.split(";").map((c) => c.trim());
      const found = cookies.find((c) => c.startsWith(cookieName + "="));
      let cookieValid = false;
      if (found) {
        const value = found.split("=")[1];
        if (value === "1") {
          // Controlla anche la scadenza: cerca la data di scadenza nel cookie (se presente)
          // ngx-cookie-service salva la scadenza come attributo expires, ma document.cookie non la espone direttamente.
          // Quindi: per sicurezza, se il cookie esiste e vale 1, verifica la scadenza confrontando la data attuale (tokenIat) con la data di scadenza stimata (1 mese da iat)
          // Se il cookie è stato impostato a partire da iat, la scadenza è iat + 1 mese
          const cookieSetDate = new Date(tokenIat); // iat = data emissione token
          const cookieExpireDate = new Date(cookieSetDate);
          cookieExpireDate.setMonth(cookieExpireDate.getMonth() + 1);
          if (cookieExpireDate > new Date(tokenIat)) {
            // Cookie ancora valido rispetto a iat
            cookieValid = true;
          }
        }
      }
      // Se cookie non trovato, valore diverso da 1, o scaduto, suggerisco cambio password
      if (!cookieValid) {
        return true;
      }
    }
    return false;
  }

  /**
   * Esegue il refresh del token e le azioni post-login.
   * @param redirectUrl URL di redirect dopo il login
   * @returns Promise<boolean>
   */
  async refreshTokenAndLoginActions(
    redirectUrl: currentUrlType,
  ): Promise<boolean> {
    try {
      const data = await firstValueFrom(this.authService.refreshToken());
      await this.postLoginActions(data, false, redirectUrl);
      return true;
    } catch (error) {
      this.logger.warn("Refresh token/login actions failed", error);
      return false;
    }
  }
}
